// @flow
import { TRACKING_EVENTS } from './events';
import { mapItemsToGA4, toNumber } from './ecommerce';

type User = {
  id: string,
  grade?: { name: string },
  rule?: string,
  bizNumber?: string,
  [key: string]: any,
};

type PageInfo = {
  path: string,
  title: string,
};

const getPageInfo = (location?: { pathname: string, search: string }): PageInfo => {
  if (typeof window === 'undefined') {
    return { path: '', title: '' };
  }
  const path = location
    ? location.pathname + location.search
    : window.location.pathname + window.location.search;
  return {
    path,
    title: document.title || '',
  };
};

export const setUser = (user: User): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  window.dataLayer.push({
    user_id: user.id,
    user_properties: {
      grade: user.grade && user.grade.name ? user.grade.name : '',
      rule: user.rule || '',
      biz_number: user.bizNumber || '',
    },
  });
};

// 로그아웃 시 dataLayer의 user_id/속성을 비워 같은 SPA 세션의 후속 이벤트에 stale user_id가 붙지 않게 한다.
export const resetUser = (): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  window.dataLayer.push({
    user_id: undefined,
    user_properties: undefined,
  });
};

export const pushPageView = (location?: { pathname: string, search: string }): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  const page = getPageInfo(location);
  window.dataLayer.push({
    event: 'pageview',
    page: {
      path: page.path,
      search: window.location.search || '',
      title: page.title,
    },
  });
};

const mapToGA4Event = (
  eventName: string,
  properties: { [key: string]: any },
): ?{ event: string, ecommerce?: Object } => {
  switch (eventName) {
    case TRACKING_EVENTS.PRODUCT_VIEWED:
      return {
        event: 'view_item',
        ecommerce: {
          items: [
            {
              item_id: properties.productId || '',
              item_name: properties.productTitle || '',
              item_brand: properties.brand || '',
            },
          ],
        },
      };
    case TRACKING_EVENTS.PRODUCT_LIST_VIEWED:
      return {
        event: 'view_item_list',
        ecommerce: {
          item_list_id: properties.listId || '',
          item_list_name: properties.listName || '',
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.PRODUCT_CART_ADDED:
      return {
        event: 'add_to_cart',
        ecommerce: {
          currency: properties.currency || 'KRW',
          value: toNumber(properties.value, toNumber(properties.price) * toNumber(properties.quantity, 1)),
          items: mapItemsToGA4([properties]),
        },
      };
    case TRACKING_EVENTS.PRODUCT_CART_REMOVED: {
      const hasItemsArray = properties.items && Array.isArray(properties.items);
      const itemsForGA4 = hasItemsArray ? properties.items : [properties];
      const computedValue = hasItemsArray
        ? itemsForGA4.reduce((sum, item) => sum + toNumber(item.value, toNumber(item.price) * toNumber(item.quantity, 1)), 0)
        : toNumber(properties.price) * toNumber(properties.quantity, 1);
      return {
        event: 'remove_from_cart',
        ecommerce: {
          currency: properties.currency || 'KRW',
          value: toNumber(properties.value, computedValue),
          items: mapItemsToGA4(itemsForGA4),
        },
      };
    }
    case TRACKING_EVENTS.CART_VIEWED:
      return {
        event: 'view_cart',
        ecommerce: {
          currency: 'KRW',
          value: toNumber(properties.value),
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.CHECKOUT_STARTED:
      return {
        event: 'begin_checkout',
        ecommerce: {
          currency: 'KRW',
          value: toNumber(properties.value),
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.PURCHASE_COMPLETED:
      // purchase는 서버사이드(Event Bus)에서 GA4 Measurement Protocol로 전송한다.
      // 비동기 결제(무통장입금/가상계좌/후불결제) 허위 전환 및 카드 결제 이중 카운트를
      // 방지하기 위해 클라이언트에서는 GA4 purchase를 push하지 않는다.
      // (Clarity hasPurchased 태그와 내부 dbTracker 신호는 track()에서 계속 발생한다.)
      return null;
    case TRACKING_EVENTS.REGISTRATION_COMPLETED:
      return {
        event: 'sign_up',
        method: properties.method || '',
      };
    case TRACKING_EVENTS.PRODUCT_WISHLIST_ADDED:
      return {
        event: 'add_to_wishlist',
        ecommerce: {
          items: [
            {
              item_id: properties.productId || '',
              item_name: properties.productTitle || '',
              item_brand: properties.brand || '',
            },
          ],
        },
      };
    case TRACKING_EVENTS.PRODUCT_SELECTED:
      return {
        event: 'select_item',
        ecommerce: {
          item_list_id: properties.listId || '',
          item_list_name: properties.listName || '',
          items: [
            {
              item_id: properties.productId || '',
              item_name: properties.productTitle || '',
              item_brand: properties.brand || '',
              index: properties.index || 0,
            },
          ],
        },
      };
    case TRACKING_EVENTS.SEARCH_PERFORMED:
      return {
        event: 'search',
        search_term: properties.searchTerm || '',
        result_count: toNumber(properties.resultCount),
        search_menu: properties.menu || '',
        search_sort: properties.sort || '',
        has_filters: !!properties.hasFilters,
      };
    case TRACKING_EVENTS.SHIPPING_INFO_ADDED:
      return {
        event: 'add_shipping_info',
        ecommerce: {
          currency: 'KRW',
          value: toNumber(properties.value),
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    case TRACKING_EVENTS.PAYMENT_INFO_ADDED:
      return {
        event: 'add_payment_info',
        ecommerce: {
          currency: 'KRW',
          value: toNumber(properties.value),
          payment_type: properties.paymentType || '',
          items: properties.items ? mapItemsToGA4(properties.items) : [],
        },
      };
    default:
      return null;
  }
};

export const push = (eventName: string, properties: { [key: string]: any } = {}): void => {
  if (typeof window === 'undefined' || !window.dataLayer) return;

  const ga4Event = mapToGA4Event(eventName, properties);
  if (ga4Event) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(ga4Event);
  }
};



// WEBPACK FOOTER //
// ./src/lib/tracking/gtmTracker.js